/**
 * GettingStartedWidget - Progress checklist for self-serve onboarding.
 *
 * Shows step labels and hints; for “Ask your first question” offers Go to Copilot
 * when the user is not already there.
 *
 * Mounted once in DashboardLayout; only renders for self_serve tenants that
 * haven't dismissed it or completed all steps.
 */
import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Paper, Typography,
  Collapse, Button,
  LinearProgress,
} from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import RocketLaunchRoundedIcon from '@mui/icons-material/RocketLaunchRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { useProductOnboarding, type ProductStep } from '../hooks/useProductOnboarding';
import { ConfirmDialog } from './dialog';
import {
  trackProductOnboardingCtaClicked,
  trackProductOnboardingWidgetDismissed,
} from '../utils/analytics';

interface GettingStartedWidgetProps {
  leftOffsetPx?: number;
  embedded?: boolean;
  hideWhenCollapsed?: boolean;
  /**
   * `mobileBanner` — full-width strip under the app bar: progress bar + horizontal step dots,
   * tap header to expand the full checklist (desktop sidebar widget unchanged).
   */
  variant?: 'standard' | 'mobileBanner';
}

export default function GettingStartedWidget({
  leftOffsetPx = 280,
  embedded = false,
  hideWhenCollapsed = false,
  variant = 'standard',
}: GettingStartedWidgetProps) {
  const { steps, completedCount, totalCount, currentStepId, shouldShow, dismiss } = useProductOnboarding();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobileBanner = variant === 'mobileBanner';
  const [expanded, setExpanded] = useState(!isMobileBanner);
  const [confirmDismiss, setConfirmDismiss] = useState(false);

  const onCopilot = location.pathname === '/copilot' || location.pathname.startsWith('/copilot/');

  if (!shouldShow) return null;
  if (!isMobileBanner && embedded && hideWhenCollapsed) return null;

  const getStepStatus = (step: ProductStep): 'completed' | 'current' | 'locked' => {
    if (step.completed) return 'completed';
    if (step.id === currentStepId) return 'current';
    return 'locked';
  };

  const handleDismissConfirm = async () => {
    setConfirmDismiss(false);
    await dismiss();
    trackProductOnboardingWidgetDismissed({ completed_count: completedCount, total_count: totalCount });
  };

  const progressPct = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const currentStep = steps.find((s) => s.id === currentStepId);

  const headerBar = (
    <Box
      onClick={() => setExpanded((e) => !e)}
      sx={(t) => ({
        pl: 2,
        pr: 1,
        py: 1.25,
        bgcolor: t.tokens.brand.primary,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: 'pointer',
        userSelect: 'none',
      })}
    >
      <RocketLaunchRoundedIcon sx={{ color: '#fff', fontSize: 16, flexShrink: 0 }} />
      <Typography
        sx={{
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.875rem',
          letterSpacing: '-0.005em',
          flex: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        Getting started
      </Typography>
      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.75rem', fontWeight: 600, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
        {completedCount}/{totalCount}
      </Typography>
      <Box
        sx={(t) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 24,
          height: 24,
          borderRadius: `${t.ds.radius.xs}px`,
          bgcolor: 'rgba(255,255,255,0.15)',
          flexShrink: 0,
          mr: 0.25,
          transition: 'background 0.15s',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.25)' },
        })}
      >
        {/* Mobile banner: up = expanded (matches tap-to-collapse). Desktop floating card: down = expanded (panel opens downward). */}
        {(isMobileBanner ? expanded : !expanded) ? (
          <KeyboardArrowUpRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
        ) : (
          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 16, color: '#fff' }} />
        )}
      </Box>
    </Box>
  );

  const horizontalStepper = (
    <Box
      sx={(t) => ({
        display: 'flex',
        alignItems: 'center',
        px: 1.5,
        py: 1,
        gap: 0,
        bgcolor: t.tokens.surface.sunken,
      })}
    >
      {steps.map((step, idx) => {
        const stepStatus = getStepStatus(step);
        const isDone = stepStatus === 'completed';
        const isCurrent = stepStatus === 'current';
        return (
          <React.Fragment key={step.id}>
            {idx > 0 && (
              <Box
                sx={(t) => ({
                  flex: 1,
                  height: 2,
                  minWidth: 4,
                  borderRadius: `${t.ds.radius.full}px`,
                  bgcolor: isDone ? t.tokens.semantic.success : t.tokens.border.subtle,
                  alignSelf: 'center',
                })}
              />
            )}
            <Box
              sx={(t) => ({
                width: 12,
                height: 12,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...(isDone && {
                  bgcolor: t.tokens.semantic.success,
                  boxShadow: `0 0 0 2px ${t.tokens.semantic.successSoft}`,
                }),
                ...(isCurrent && {
                  bgcolor: t.tokens.brand.primary,
                  boxShadow: `0 0 0 2px ${t.tokens.brand.primarySoft}`,
                }),
                ...(!isDone && !isCurrent && {
                  bgcolor: 'transparent',
                  border: `2px solid ${t.tokens.border.default}`,
                }),
              })}
            >
              {isDone && <CheckRoundedIcon sx={{ fontSize: 8, color: '#fff' }} />}
              {isCurrent && !isDone && (
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#fff' }} />
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );

  const verticalStepsBlock = (
    <Box sx={{ px: 2, pt: 2, pb: 1 }}>
      {steps.map((step, idx) => {
        const stepStatus = getStepStatus(step);
        const isCurrent = stepStatus === 'current';
        const isDone = stepStatus === 'completed';
        const isLast = idx === steps.length - 1;

        return (
          <Box key={step.id} sx={{ display: 'flex', gap: 1.5 }}>
            {/* Left: node + connecting line */}
            <Box sx={{
              display: 'flex', flexDirection: 'column', alignItems: 'center',
              width: 20, flexShrink: 0,
            }}>
              {/* Node */}
              <Box sx={(t) => ({
                width: 20, height: 20,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                ...(isDone && {
                  bgcolor: t.tokens.semantic.success,
                  boxShadow: `0 0 0 3px ${t.tokens.semantic.successSoft}`,
                }),
                ...(isCurrent && {
                  bgcolor: t.tokens.brand.primary,
                  boxShadow: `0 0 0 3px ${t.tokens.brand.primarySoft}`,
                }),
                ...(!isDone && !isCurrent && {
                  bgcolor: 'transparent',
                  border: `2px solid ${t.tokens.border.default}`,
                }),
              })}>
                {isDone && <CheckRoundedIcon sx={{ fontSize: 12, color: '#fff', fontWeight: 900 }} />}
                {isCurrent && (
                  <Box sx={{
                    width: 7, height: 7, borderRadius: '50%', bgcolor: '#fff',
                  }} />
                )}
              </Box>

              {/* Connector line */}
              {!isLast && (
                <Box sx={(t) => ({
                  width: 2,
                  flex: 1,
                  minHeight: 16,
                  mt: 0.5,
                  mb: 0.5,
                  borderRadius: `${t.ds.radius.full}px`,
                  bgcolor: isDone
                    ? t.tokens.semantic.success
                    : isCurrent
                      ? t.tokens.brand.primarySoft
                      : t.tokens.border.subtle,
                  transition: 'background 0.3s',
                })} />
              )}
            </Box>

            {/* Right: label + current-step hint */}
            <Box sx={{
              flex: 1, minWidth: 0, overflow: 'hidden',
              pb: isLast ? 0 : 1.5,
            }}>
              <Box sx={(t) => ({
                borderRadius: `${t.ds.radius.sm}px`,
                px: isCurrent ? 1.25 : 0,
                py: isCurrent ? 1 : 0.25,
                bgcolor: isCurrent ? t.tokens.brand.primarySofter : 'transparent',
                border: isCurrent ? `1px solid ${t.tokens.brand.primarySoft}` : '1px solid transparent',
                transition: 'all 0.2s',
              })}>
                <Typography sx={(t) => ({
                  fontSize: '0.8125rem',
                  fontWeight: isCurrent ? 600 : 500,
                  color: isDone
                    ? t.tokens.text.disabled
                    : isCurrent
                      ? t.tokens.text.primary
                      : t.tokens.text.tertiary,
                  textDecoration: isDone ? 'line-through' : 'none',
                  lineHeight: 1.4,
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere',
                })}>
                  {step.label}
                </Typography>
                {isCurrent && step.id === 'first_question_asked' && (
                  <>
                    {!onCopilot && (
                      <>
                        {step.description?.trim() && (
                          <Typography
                            sx={(t) => ({
                              mt: 0.75,
                              fontSize: '0.75rem',
                              color: t.tokens.text.secondary,
                              lineHeight: 1.5,
                              display: 'block',
                            })}
                          >
                            {step.description}
                          </Typography>
                        )}
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: '14px !important' }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            trackProductOnboardingCtaClicked({
                              step_id: 'first_question_asked',
                              cta_route: '/copilot',
                              from_path: location.pathname,
                            });
                            navigate('/copilot');
                          }}
                          sx={(t) => ({
                            mt: 1,
                            py: 0.5,
                            px: 1.25,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: `${t.ds.radius.sm}px`,
                            width: '100%',
                          })}
                        >
                          Go to Copilot
                        </Button>
                      </>
                    )}
                    {onCopilot && (
                      <Typography
                        sx={(t) => ({
                          mt: 0.75,
                          fontSize: '0.75rem',
                          color: t.tokens.text.secondary,
                          lineHeight: 1.5,
                          display: 'block',
                        })}
                      >
                        {step.description?.trim()
                          ? `${step.description.trim()} Press Send when you're ready.`
                          : 'Type your question in the box on this page and press Send.'}
                      </Typography>
                    )}
                  </>
                )}
                {isCurrent && step.id !== 'first_question_asked' && step.description?.trim() && (
                  <Typography
                    sx={(t) => ({
                      mt: 0.75,
                      fontSize: '0.75rem',
                      color: t.tokens.text.secondary,
                      lineHeight: 1.5,
                      display: 'block',
                    })}
                  >
                    {step.description}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </Box>
  );

  const dismissBlock = (
    <Box sx={{ pb: 1.5, textAlign: 'center' }}>
      <Button
        size="small"
        onClick={() => setConfirmDismiss(true)}
        sx={(t) => ({
          fontSize: '0.75rem',
          color: t.tokens.text.tertiary,
          textTransform: 'none',
          '&:hover': { color: t.tokens.text.secondary, bgcolor: 'transparent' },
        })}
      >
        Skip this guide
      </Button>
    </Box>
  );

  const collapseContent = (
    <>
      {verticalStepsBlock}
      {dismissBlock}
    </>
  );

  const dismissDialog = (
    <ConfirmDialog
      open={confirmDismiss}
      onClose={() => setConfirmDismiss(false)}
      onConfirm={handleDismissConfirm}
      title="Skip the guide?"
      description="You can always find this checklist again from the Help menu."
      confirmLabel="Skip guide"
      tone="destructive"
    />
  );

  if (isMobileBanner) {
    return (
      <>
        <Paper
          elevation={0}
          sx={{
            position: 'relative',
            width: '100%',
            borderRadius: 0,
            overflow: 'hidden',
            border: 'none',
          }}
        >
          {headerBar}
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={(t) => ({
              height: 3,
              borderRadius: 0,
              bgcolor: t.tokens.brand.primarySoft,
              '& .MuiLinearProgress-bar': {
                borderRadius: 0,
                bgcolor: t.tokens.brand.primary,
              },
            })}
          />
          {horizontalStepper}
          {currentStep && (
            <Typography
              sx={(t) => ({
                display: 'block',
                px: 2,
                py: 0.75,
                fontSize: '0.75rem',
                fontWeight: 600,
                color: t.tokens.text.secondary,
                borderBottom: `1px solid ${t.tokens.border.subtle}`,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              })}
              title={currentStep.label}
            >
              Now: {currentStep.label}
            </Typography>
          )}
          <Collapse in={expanded}>{collapseContent}</Collapse>
        </Paper>
        {dismissDialog}
      </>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={(t) => ({
          position: embedded ? 'relative' : 'fixed',
          bottom: embedded ? 'auto' : 24,
          left: embedded ? 'auto' : { xs: 16, md: leftOffsetPx + 16 },
          width: embedded ? '100%' : 300,
          borderRadius: `${t.ds.radius.lg}px`,
          overflow: 'hidden',
          zIndex: embedded ? 'auto' : 1200,
          bgcolor: t.tokens.surface.raised,
          border: `1px solid ${t.tokens.border.default}`,
          boxShadow: embedded ? 'none' : t.tokens.shadow.lg,
        })}
      >
        {headerBar}
        <Collapse in={expanded}>{collapseContent}</Collapse>
      </Paper>
      {dismissDialog}
    </>
  );
}
